<script setup lang="ts">
import { CssVariables, type CssVariableTypes } from '@assets/styles/types';
import { useClaims } from '@auth/useClaims';
import { VButton } from '@shared/component/VButton';
import VContentBox from '@shared/component/VContentBox/VContentBox.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import { adviseurApi } from '@services/crm-internal/service';
import { brandingQueries } from '@services/overig/queries/BrandingQueries';
import { brandingApi } from '@services/overig/service';
import VButtonGroup from '@shared/component/VButtonGroup/VButtonGroup.vue';
import VSizeContainer from '@shared/layout/VSizeContainer/VSizeContainer.vue';
import { useNotification } from '@shared/plugin/prime-vue/useNotification';
import { useMutation, useQuery } from '@tanstack/vue-query';
import { computed, ref, watch } from 'vue';
import StringField from '@shared/form/StringField/StringField.vue';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import FieldRowGroup from '@shared/form/FieldRowV2/FieldRowGroup.vue';
import SelectField from '@shared/form/SelectField/SelectField.vue';
import { rgbToHex, hexToRgb } from '@shared/styleguide/colorUtils';
import VHeading from '@shared/typography/VHeading/VHeading.vue';

const notification = useNotification();
const claims = useClaims();

const adviseurnummer = ref<number>();
const showReset = ref(false);
const variables = ref<{ [p: string]: string }>({});

const colorToHex = (color: string | undefined, type: CssVariableTypes) =>
  color && type === 'rgb-color' ? rgbToHex(color) : color || '';

const hexToColor = (color: string | undefined, type: CssVariableTypes) =>
  color && type === 'rgb-color' ? hexToRgb(color) : color || '';

function setStyle(reset: boolean = false) {
  const cssVariables = document.querySelector(':root') as HTMLStyleElement;
  Object.entries(variables.value).forEach((v) => {
    cssVariables.style.setProperty(v[0], reset ? null : v[1]);
  });
  showReset.value = !reset;
}

const { data: adviseurData } = useQuery({
  queryFn: () => adviseurApi.tussenpersonenGet(),
  queryKey: ['tussenpersonenGet'],
  select: (result) =>
    result.data.result
      ?.filter((adviseur) => adviseur.id > 0)
      .map((adviseur) => ({ value: adviseur.id, label: `${adviseur.name} (${adviseur.id})` })),
  enabled: computed(() => claims.value?.isInternal ?? false),
});

const { data: brandingData, isFetching: isFetchingBranding } = useQuery({
  ...brandingQueries.fetchBranding(adviseurnummer),
  select: (data) => data.data.result.variables,
});

watch(
  () => brandingData.value,
  (d) => (variables.value = JSON.parse(JSON.stringify(d ?? {}))),
);

const { mutate, isPending: saveLoading } = useMutation({
  mutationFn: () => brandingApi.brandingPost(adviseurnummer.value, variables.value),
  onSuccess: () => notification.success('Branding succesvol opgeslagen.'),
});
</script>

<template>
  <VSizeContainer class="my-4 flex flex-col gap-4" size="deprecated_1200">
    <VContentBox v-if="claims?.isInternal">
      <SelectField
        :options="adviseurData"
        v-model:value="adviseurnummer"
        placeholder="Selecteer adviseur (interne optie)"
      />
    </VContentBox>

    <VContentBox>
      <div class="flex justify-between">
        <VHeading :level="3">Branding variabelen aanpassen</VHeading>
        <div>
          <VButton
            v-if="showReset"
            @click="setStyle(true)"
            class="mr-2"
            inverted
            prepend-icon="fas fa-rotate-left"
          >
            Reset
          </VButton>
          <VButton
            :disabled="isFetchingBranding"
            inverted
            class="mr-2"
            @click="setStyle()"
            :loading="saveLoading"
            prepend-icon="fas fa-sparkles"
          >
            Voorbeeld
          </VButton>
        </div>
      </div>
      <hr />
      <VSkeleton height="lg" :count="10" v-if="isFetchingBranding" />
      <form v-else>
        <FieldRowGroup>
          <FieldRowV2 :key="cssVar" v-for="({ type }, cssVar) in CssVariables" :label="cssVar">
            <StringField
              :placeholder="
                type === 'font'
                  ? 'Vul de naam van een lettertype in'
                  : 'Vul een hexcode in of klik op de kleur'
              "
              :disabled="saveLoading"
              :value="colorToHex(variables[cssVar], type)"
              @update:value="(newValue) => (variables[cssVar] = hexToColor(newValue, type))"
            />
            <span
              v-if="type === 'font'"
              :style="{
                'font-family': variables[cssVar],
                'text-align': 'right',
                width: '8rem',
              }"
            >
              Voorbeeld
            </span>
            <input
              v-else
              class="cursor-pointer border-0 p-0"
              type="color"
              :value="colorToHex(variables[cssVar], type)"
              @input="
                variables[cssVar] = hexToColor(($event.target as HTMLInputElement).value, type)
              "
            />
          </FieldRowV2>
        </FieldRowGroup>
        <VButtonGroup align="right" class="pt-4">
          <VButton @click="mutate()" :loading="saveLoading" prepend-icon="fas fa-save"
            >Opslaan</VButton
          >
        </VButtonGroup>
      </form>
    </VContentBox>
  </VSizeContainer>
</template>
